import { ArrowLeftIcon, ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/20/solid";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import { ReturnDeadlineKind, ReturnDetail } from "../../models/returns/IReturnsApi";
import classNames from "../../utils/classNames";
import { deadlineText } from "../../utils/returnsRules";
import ActionMenu, { ActionMenuItem } from "../shared/ActionMenu";
import ChannelTag from "./ChannelTag";

const DEADLINE_RULES: Record<ReturnDeadlineKind, string> = {
  respond: "eBay's deadline · 3 business days",
  post: "eBay's send-by date",
  buyer_respond: "eBay's deadline",
  refund: "eBay's deadline · 2 business days",
};

interface ReturnHeaderProps {
  detail: ReturnDetail;
  now: DateTime;
  /** Neighbours in the list this return was opened from. */
  prevId?: string;
  nextId?: string;
}

/** Back, prev/next, who and what, the deadline that applies now and the links menu. */
export default function ReturnHeader({ detail, now, prevId, nextId }: ReturnHeaderProps) {
  const { t } = useTranslation("returns");
  const navigate = useNavigate();
  const deadline = deadlineText(detail.deadline, now);
  const isEbay = detail.channel === "ebay";

  const open = (url: string | null) => () => {
    if (!url) return;
    if (/^https?:/i.test(url)) window.open(url, "_blank", "noopener");
    else navigate(url);
  };
  const links = detail.links;
  const items: ActionMenuItem[] = [
    { label: `${t("Open return on eBay")} ↗`, onClick: open(links.ebayReturnUrl), hidden: !links.ebayReturnUrl },
    { label: `${t("Open order on eBay")} ↗`, onClick: open(links.ebayOrderUrl), hidden: !links.ebayOrderUrl },
    { label: t("View part in Wilma"), onClick: open(links.partUrl), hidden: !links.partUrl },
    { label: t("View order in Wilma"), onClick: open(links.orderUrl), hidden: !links.orderUrl },
    { label: detail.order?.invoiceNumber ? t("View invoice {{number}}", { number: detail.order.invoiceNumber }) : t("View invoice"), onClick: open(links.invoiceUrl), hidden: !links.invoiceUrl },
    { label: t("Open conversation in Messages"), onClick: open(links.conversationUrl), hidden: !links.conversationUrl },
    { label: t("Open ticket"), onClick: open(links.ticketUrl), hidden: !links.ticketUrl },
  ];

  const navButton = "flex h-7 w-7 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40";

  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-gray-200 bg-white px-4 py-2.5">
      <Link to="/returns" aria-label={t("Back to returns")} className="flex h-[30px] w-[30px] items-center justify-center rounded-lg text-gray-700 hover:bg-gray-100">
        <ArrowLeftIcon className="h-[18px] w-[18px]" />
      </Link>
      <button type="button" aria-label={t("Previous return")} disabled={!prevId} onClick={() => prevId && navigate(`/returns/${prevId}`)} className={navButton}>
        <ChevronLeftIcon className="h-4 w-4" />
      </button>
      <button type="button" aria-label={t("Next return")} disabled={!nextId} onClick={() => nextId && navigate(`/returns/${nextId}`)} className={navButton}>
        <ChevronRightIcon className="h-4 w-4" />
      </button>
      <ChannelTag channel={detail.channel} className="ml-1 !px-[7px] !py-[3px] !text-[11px] !leading-none" />
      <span className="text-[14px] font-semibold text-gray-900">{detail.buyer.username}</span>
      <span className="min-w-0 truncate text-[12px] text-gray-500">
        · {detail.part?.title ?? t("Part not linked")}
        {detail.part?.tagNumber ? ` · ${detail.part.tagNumber}` : ""}
      </span>
      <div className="ml-auto flex items-center gap-2">
        {deadline && (
          <div className={classNames("flex items-center gap-2 rounded-lg border px-2.5 py-1", deadline.tone === "red" ? "border-red-200 bg-red-50" : deadline.tone === "amber" ? "border-amber-200 bg-amber-50" : "border-gray-200 bg-gray-50")}>
            <span className={classNames("text-[16px] font-bold leading-none", deadline.tone === "red" ? "text-red-700" : deadline.tone === "amber" ? "text-amber-700" : "text-gray-700")}>{deadline.text}</span>
            <span className="flex flex-col leading-tight">
              <span className={classNames("text-[11px] font-semibold", deadline.tone === "red" ? "text-red-900" : deadline.tone === "amber" ? "text-amber-900" : "text-gray-700")}>{deadline.sub}</span>
              <span className={classNames("text-[10px]", deadline.tone === "red" ? "text-red-900" : deadline.tone === "amber" ? "text-amber-900" : "text-gray-500")}>
                {isEbay ? (detail.deadline.kind ? t(DEADLINE_RULES[detail.deadline.kind]) : "") : t("PartPro window")}
              </span>
            </span>
          </div>
        )}
        <ActionMenu label={t("More")} items={items} classes="!border-gray-200 !bg-white !text-gray-700 hover:!bg-gray-50 !shadow-none" />
      </div>
    </div>
  );
}
